/**
 * PaymentsSection — "Quote & Payment" dashboard section (#payments).
 *
 * Replaces the deleted File Trademark wizard. In the quote-first funnel the
 * marketing wizard is the intake; this section is where a visitor lands (via
 * the report email's magic link) to review their confirmed quote, sign the
 * declaration, and pay.
 *
 * UI states are driven by application.status:
 *   - quote_requested  → "Your report is being prepared" (24-hour promise)
 *   - quote_sent       → invoice-style quote document + collapsible report +
 *                        applicant details (name + postal address, autosaved
 *                        as they type — IP Australia requires both to file) +
 *                        single-checkbox declaration + Stripe Embedded
 *                        Checkout, which loads immediately and is soft-gated
 *                        by an overlay until the applicant essentials are in
 *                        AND the declaration is ticked
 *   - paid / post-paid → payment completion card + filing status (ApplicationStatusCard)
 *
 * Data comes from TanStack Query (usePricingQuery / useDeclarationQuery) and
 * PaymentContext — no wizard contexts. Rendering discipline: checkoutOptions and
 * line items are memoized and the Embedded Checkout is keyed by clientSecret so
 * unrelated re-renders never remount it mid-session. The report lives in its
 * own components (CollapsibleReport/ReportView) with their own queries so
 * report fetches and expansion toggles never touch the payment cards.
 */
import React, { useState, useMemo, useCallback, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import i18n from '../../../i18n';
import {
  AlertCircle,
  Loader2,
  Clock,
  FileText,
  ExternalLink,
  Lock
} from 'lucide-react';
import { loadStripe } from '@stripe/stripe-js';
import {
  EmbeddedCheckoutProvider,
  EmbeddedCheckout
} from '@stripe/react-stripe-js';
import {
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardContent
} from '../../../common';
import { Button } from '../../../common';
import { H3, Text } from '../../../common/Typography';
import { Stack, Divider } from '../../../common/layout/Layout';
import { useUser } from '@clerk/clerk-react';
import { useUserApplication } from '../../../context/UserApplicationContext';
import { usePaymentContext } from '../../../context/PaymentContext';
import { usePricingQuery } from '../../../hooks/queries/pricing';
import { useDeclarationQuery } from '../../../hooks/queries/declaration';
import { paymentService } from '../../../services/api/paymentService';
import { ApplicationStatusCard } from '../../ApplicationStatus';
import PaymentStatusBadge from './PaymentStatusBadge';
import PaymentCompletionCard from './PaymentCompletionCard';
import DeclarationCard from './DeclarationCard';
import ApplicantDetailsCard from './ApplicantDetailsCard';
import ReportView from './ReportView';
import CollapsibleReport from './CollapsibleReport';
import { MARKETING_WIZARD_URL } from '../../../utils/applicationStatus';

// Initialize Stripe once at module scope
const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY);

const ZERO_DECIMAL_CURRENCIES = ['JPY', 'KRW'];

// Full names for the postal-address country (same set the old guest checkout
// offered); falls back to the uppercase code for anything else
const COUNTRY_FULL_NAMES = {
  au: 'Australia',
  us: 'United States',
  uk: 'United Kingdom',
  gb: 'United Kingdom',
  eu: 'European Union',
  ca: 'Canada',
  nz: 'New Zealand',
  sg: 'Singapore',
  jp: 'Japan',
  cn: 'China',
  in: 'India',
};

const countryFullName = (country) => {
  if (!country) return '';
  if (typeof country === 'object') {
    const code = (country.code || country.countryId || '').toLowerCase();
    return country.name || COUNTRY_FULL_NAMES[code] || (code ? code.toUpperCase() : '');
  }
  const code = String(country).toLowerCase();
  return COUNTRY_FULL_NAMES[code] || String(country).toUpperCase();
};

const countryDisplayName = (country) => {
  if (!country) return '';
  if (typeof country === 'string') return country.toUpperCase();
  return country.name || (country.code ? country.code.toUpperCase() : '') || (country.countryId ? country.countryId.toUpperCase() : '');
};

const classDisplayLabel = (cls) => {
  if (cls === null || cls === undefined) return '';
  if (typeof cls === 'number' || typeof cls === 'string') return `Class ${cls}`;
  const number = cls.classNumber ?? cls.number;
  return cls.className ? `Class ${number}: ${cls.className}` : `Class ${number}`;
};

const capitalize = (str) => (str ? str.charAt(0).toUpperCase() + str.slice(1) : '');

const PaymentsSection = () => {
  const { t } = useTranslation('dashboard');
  const { t: tPayment } = useTranslation('payment');
  const { user } = useUser();
  const { currentApplication, currentApplicationId } = useUserApplication();
  const {
    createCheckoutSession: startCheckoutSession,
    clientSecret: contextClientSecret,
    clientSecretApplicationId: contextClientSecretApplicationId,
  } = usePaymentContext();

  const {
    data: pricingRecord,
    isLoading: pricingLoading,
    isError: pricingError,
  } = usePricingQuery(currentApplicationId);

  const {
    data: declaration,
    isLoading: declarationLoading,
  } = useDeclarationQuery(currentApplicationId);

  const [clientSecret, setClientSecret] = useState(() =>
    contextClientSecret && contextClientSecretApplicationId === currentApplicationId
      ? contextClientSecret
      : ''
  );
  const [isCreatingSession, setIsCreatingSession] = useState(false);
  const [error, setError] = useState(null);
  const checkoutInitialized = useRef(false);
  const autoInitAttempted = useRef(false);
  const declarationRef = useRef(null);
  const applicantRef = useRef(null);

  // Live applicant state reported up from ApplicantDetailsCard — drives the
  // quote card's Applicant row and the payment overlay gate
  const [applicantInfo, setApplicantInfo] = useState({ name: '', complete: false });
  const handleApplicantChange = useCallback((info) => {
    setApplicantInfo((prev) =>
      prev.name === info.name && prev.complete === info.complete ? prev : info
    );
  }, []);

  // If the active application changes, discard any checkout state that was
  // created for the previous application.
  const clientSecretAppIdRef = useRef(currentApplicationId);
  useEffect(() => {
    if (clientSecretAppIdRef.current !== currentApplicationId) {
      clientSecretAppIdRef.current = currentApplicationId;
      setClientSecret('');
      checkoutInitialized.current = false;
      autoInitAttempted.current = false;
      setError(null);
    }
  }, [currentApplicationId]);

  // Sync with a client secret already held by PaymentContext (e.g. re-entry) —
  // only when it was minted for the currently active application.
  useEffect(() => {
    if (
      contextClientSecret &&
      contextClientSecretApplicationId === currentApplicationId &&
      !clientSecret &&
      !checkoutInitialized.current
    ) {
      setClientSecret(contextClientSecret);
      checkoutInitialized.current = true;
    }
  }, [contextClientSecret, contextClientSecretApplicationId, currentApplicationId, clientSecret]);

  // ---- Derived application state -----------------------------------------
  const status = currentApplication?.status;
  const isRefunded = currentApplication?.paymentStatus === 'refunded' || status === 'refunded';
  const isPartiallyRefunded = currentApplication?.paymentStatus === 'partially_refunded' || status === 'partially_refunded';
  const isPaid = !isRefunded && !isPartiallyRefunded && (
    currentApplication?.paymentStatus === 'paid' ||
    ['payment_completed', 'pending_attorney_filing', 'filing_in_progress', 'filed', 'registered', 'completed'].includes(status)
  );

  const declarationCompleted = Boolean(declaration?.poaProvided && declaration?.declarationProvided);

  // Signature auto-fill for the single-checkbox declaration — the signed-in
  // Clerk user's full name (same pattern the old guest checkout used with
  // the contact name)
  const signerName = user?.fullName
    || [user?.firstName, user?.lastName].filter(Boolean).join(' ')
    || user?.primaryEmailAddress?.emailAddress
    || '';

  // Applicant-name prefill deliberately excludes the email fallback — an
  // email address is never a filing applicant name
  const applicantFallbackName = user?.fullName
    || [user?.firstName, user?.lastName].filter(Boolean).join(' ')
    || '';

  // ---- Quote / pricing derivations (all memoized) ------------------------
  const currency = (pricingRecord?.currency || 'USD').toUpperCase();
  const zeroDecimal = ZERO_DECIMAL_CURRENCIES.includes(currency);

  const formatAmount = useCallback((amount) => {
    if (amount === null || amount === undefined || Number.isNaN(Number(amount))) return '—';
    try {
      return new Intl.NumberFormat('en-AU', { style: 'currency', currency }).format(Number(amount));
    } catch (e) {
      return `${Number(amount).toFixed(2)} ${currency}`;
    }
  }, [currency]);

  const quoteTotal = useMemo(() => {
    const total = Number(pricingRecord?.pricing?.total);
    return Number.isFinite(total) && total > 0 ? total : null;
  }, [pricingRecord?.pricing?.total]);

  const lineItems = useMemo(() => {
    if (!pricingRecord || !quoteTotal) return [];
    const items = [];
    const totals = pricingRecord.pricing || {};
    const discount = Number(totals.discount) || 0;
    const classCount = pricingRecord.classCount || pricingRecord.selectedClasses?.length || 1;
    const classText = classCount > 1 ? `${classCount} classes` : '1 class';

    if (pricingRecord.serviceFeeTotal != null || pricingRecord.govFeeTotal != null) {
      if (pricingRecord.serviceFeeTotal != null) {
        items.push({ description: t('payments.attorneyServiceFee', 'Trade mark attorney service fee'), amount: Number(pricingRecord.serviceFeeTotal) || 0 });
      }
      if (pricingRecord.govFeeTotal != null) {
        items.push({ description: `${t('payments.governmentFees', 'Government filing fees')} (${classText})`, amount: Number(pricingRecord.govFeeTotal) || 0 });
      }
    } else {
      const breakdown = Array.isArray(pricingRecord.countryBreakdown) ? pricingRecord.countryBreakdown : null;
      if (breakdown && breakdown.length > 0) {
        breakdown.forEach((row) => {
          items.push({
            description: `${row.countryName || countryDisplayName(row.countryId)} filing (${classText})`,
            amount: Number(row.price) || 0,
          });
        });
      } else {
        const countries = pricingRecord.selectedCountries || [];
        const countryText = countries.length > 0
          ? countries.map(countryDisplayName).filter(Boolean).join(', ')
          : null;
        items.push({
          description: countryText
            ? `${t('payments.filingFee', 'Trademark filing')} — ${countryText} (${classText})`
            : `${t('payments.filingFee', 'Trademark filing')} (${classText})`,
          amount: quoteTotal + discount,
        });
      }
    }

    if (discount > 0) {
      items.push({ description: t('payments.discount', 'Multi-country discount'), amount: -discount });
    }

    return items;
  }, [pricingRecord, quoteTotal, t]);

  const expectedAmountMinor = useMemo(() => {
    if (!quoteTotal) return null;
    return Math.round(quoteTotal * (zeroDecimal ? 1 : 100));
  }, [quoteTotal, zeroDecimal]);

  // Memoized so re-renders never reinitialize the Embedded Checkout
  const checkoutOptions = useMemo(() => {
    if (!clientSecret) return null;
    return { clientSecret };
  }, [clientSecret]);

  // Postal-address country is fixed to the quote's first country
  const quoteCountryName = useMemo(
    () => countryFullName(pricingRecord?.selectedCountries?.[0]),
    [pricingRecord?.selectedCountries]
  );

  // Quote document derivations. A re-quoted client (a class added, say) has a
  // revision suffix, and this has to match the numbering on the PDF they were
  // emailed — see quotePdfService.
  const quoteRevision = Number(pricingRecord?.quoteRevision) || 1;
  const quoteNumber = currentApplicationId
    ? `Q-${String(currentApplicationId).slice(0, 8).toUpperCase()}${quoteRevision > 1 ? `-R${quoteRevision}` : ''}`
    : '';
  const quoteDate = pricingRecord?.updatedAt || pricingRecord?.createdAt || null;

  // ---- Handlers ----------------------------------------------------------
  const handlePay = useCallback(async () => {
    if (!user || !currentApplicationId || !expectedAmountMinor) {
      setError('Missing application or pricing information');
      return;
    }

    setIsCreatingSession(true);
    setError(null);

    try {
      const stripeLocaleMap = { en: 'en', es: 'es', de: 'de', zh: 'zh' };
      const sessionData = {
        applicationId: currentApplicationId,
        // The backend recomputes the charge from the pricing record and rejects
        // any mismatch — this amount is informational/validated, never trusted.
        amount: expectedAmountMinor,
        currency: currency.toLowerCase(),
        customerEmail: user.emailAddresses?.[0]?.emailAddress,
        pricing: lineItems,
        locale: stripeLocaleMap[i18n.language] || 'auto',
      };

      const response = await startCheckoutSession(sessionData);
      setClientSecret(response.clientSecret);
      checkoutInitialized.current = true;
    } catch (err) {
      console.error('Error creating checkout session:', err);
      setError(err?.response?.data?.message || err.message || 'Failed to initialize payment');
    } finally {
      setIsCreatingSession(false);
    }
  }, [currency, currentApplicationId, expectedAmountMinor, lineItems, startCheckoutSession, user]);

  // Payment loads immediately: create the checkout session as soon as the
  // quote screen can render (status quote_sent / legacy unpaid with a stored
  // quote, pricing loaded, user present). The declaration does NOT gate
  // session creation — the rendered checkout is soft-gated by the overlay
  // below until the declaration is ticked.
  useEffect(() => {
    if (autoInitAttempted.current) return;
    if (!currentApplication || isPaid || isRefunded || isPartiallyRefunded) return;
    if (status === 'quote_requested') return;
    if (!user || !currentApplicationId || !expectedAmountMinor) return;
    if (clientSecret || checkoutInitialized.current) return;
    autoInitAttempted.current = true;
    handlePay();
  }, [
    currentApplication,
    isPaid,
    isRefunded,
    isPartiallyRefunded,
    status,
    user,
    currentApplicationId,
    expectedAmountMinor,
    clientSecret,
    handlePay,
  ]);

  const handleViewReceipt = useCallback(async () => {
    try {
      if (!currentApplication?.paymentId) {
        alert('Receipt not available - no payment ID found');
        return;
      }
      const receiptData = await paymentService.getReceiptUrl(currentApplication.paymentId);
      if (receiptData.receiptUrl) {
        window.open(receiptData.receiptUrl, '_blank');
      } else {
        alert('Receipt not available yet. Please try again later.');
      }
    } catch (err) {
      console.error('Error fetching receipt:', err);
      alert('Unable to load receipt. Please try again later.');
    }
  }, [currentApplication?.paymentId]);

  // Scroll to whatever is still blocking payment: applicant details first,
  // then the declaration
  const scrollToBlocking = useCallback(() => {
    const target = !applicantInfo.complete ? applicantRef.current : declarationRef.current;
    target?.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }, [applicantInfo.complete]);

  // ---- Render states -----------------------------------------------------

  // No application at all — the marketing wizard is the intake
  if (!currentApplication) {
    return (
      <Card className="mb-6">
        <CardContent className="text-center py-12">
          <FileText className="h-12 w-12 text-gray-300 mx-auto mb-4" />
          <H3 className="mb-2">{t('payments.noApplicationTitle', 'No application yet')}</H3>
          <Text className="text-gray-600 mb-6 max-w-md mx-auto">
            {t('payments.noApplicationMsg', 'Start with a free trademark check on our website. We will email you a detailed report and quote within 24 hours.')}
          </Text>
          <Button
            onClick={() => { window.location.href = MARKETING_WIZARD_URL; }}
            className="bg-[#004d40] hover:bg-[#00695c] text-white px-6 py-3 rounded-xl inline-flex items-center gap-2"
          >
            {t('payments.startFreeCheck', 'Start your free check')}
            <ExternalLink className="h-4 w-4" />
          </Button>
        </CardContent>
      </Card>
    );
  }

  // Paid (or refunded) — completion card + filing status
  if (isPaid || isRefunded || isPartiallyRefunded) {
    const paymentAmountMinor = expectedAmountMinor || (quoteTotal ? Math.round(quoteTotal * 100) : null);
    return (
      <div className="space-y-6">
        {/* Report as a SIBLING (never a wrapper) — its query lives inside
            ReportView so report fetches can't re-render the payment cards */}
        {isPaid && <ReportView applicationId={currentApplicationId} />}
        <PaymentCompletionCard
          application={currentApplication}
          paymentAmount={paymentAmountMinor}
          paymentCurrency={currency}
          paymentDate={currentApplication?.paidAt || currentApplication?.updatedAt || currentApplication?.createdAt}
          isRefunded={isRefunded}
          isPartiallyRefunded={isPartiallyRefunded}
          onViewReceipt={handleViewReceipt}
        />
        {!isRefunded && (
          <ApplicationStatusCard application={currentApplication} />
        )}
      </div>
    );
  }

  // Report requested — quote not sent yet
  if (status === 'quote_requested') {
    return (
      <Card className="mb-6 border-blue-200 bg-blue-50/40">
        <CardContent className="text-center py-12">
          <div className="mx-auto h-16 w-16 bg-blue-100 rounded-full flex items-center justify-center mb-4">
            <Clock className="h-8 w-8 text-blue-600" />
          </div>
          <H3 className="mb-2">{t('payments.reportPreparingTitle', 'Your report is being prepared')}</H3>
          <Text className="text-gray-600 max-w-md mx-auto">
            {t('payments.reportPreparingMsg', 'Our team is reviewing your trademark and preparing your detailed report and quote. We will email it to you within 24 hours.')}
          </Text>
          {currentApplication?.trademarkName && (
            <Text className="text-sm text-gray-500 mt-4">
              {t('payments.reportForTrademark', 'Trademark: {{name}}', { name: currentApplication.trademarkName })}
            </Text>
          )}
        </CardContent>
      </Card>
    );
  }

  // Pricing still loading — but once a checkout session exists, the quote
  // screen below must keep rendering so a background pricing refetch can
  // never unmount a mounted Stripe Embedded Checkout mid-payment.
  if (pricingLoading && !clientSecret) {
    return (
      <Card className="mb-6">
        <CardContent className="flex items-center justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-[#00796b] mr-3" />
          <Text>{t('payments.loadingQuote', 'Loading your quote…')}</Text>
        </CardContent>
      </Card>
    );
  }

  // Pricing failed or no quote on the record yet (never once checkout is live)
  if ((pricingError || !quoteTotal) && !clientSecret) {
    return (
      <Card className="mb-6">
        <CardContent className="text-center py-12">
          <AlertCircle className="h-10 w-10 text-amber-500 mx-auto mb-4" />
          <H3 className="mb-2">{t('payments.noQuoteTitle', 'Your quote is not ready yet')}</H3>
          <Text className="text-gray-600 max-w-md mx-auto">
            {t('payments.noQuoteMsg', 'We are still finalising the quote for this application. You will receive an email as soon as it is ready. If you think this is a mistake, reply to any of our emails and we will sort it out.')}
          </Text>
        </CardContent>
      </Card>
    );
  }

  // quote_sent (and legacy unpaid statuses with a stored quote):
  // invoice-style quote document → collapsible report → declaration → payment
  return (
    <div className="space-y-6">
      {/* Quote document card — styled like the attached quote PDF */}
      <Card>
        <CardHeader divided>
          <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between sm:gap-4">
            <div>
              <CardTitle className="text-xl">{t('payments.quoteDocTitle', 'Quote')}</CardTitle>
              <CardDescription>
                {status === 'quote_sent'
                  ? t('payments.quoteSubtitleConfirmed', 'Confirmed by our attorneys — this is the exact amount you will be charged.')
                  : t('payments.quoteSubtitle', 'Review your filing fees and complete payment')}
              </CardDescription>
            </div>
            <div className="sm:text-right flex-shrink-0">
              {currentApplication?.paymentStatus && (
                <PaymentStatusBadge
                  paymentStatus={currentApplication.paymentStatus}
                  status={status}
                  className="mb-2"
                />
              )}
              <Text className="text-sm font-semibold text-gray-900">{quoteNumber}</Text>
              <Text className="text-xs text-gray-500">
                {t('payments.dateOfIssue', 'Date of issue')}:{' '}
                {new Date(quoteDate || Date.now()).toLocaleDateString()}
              </Text>
              <Text className="text-xs text-gray-500">{t('payments.quoteValidity', 'Valid for 30 days')}</Text>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {/* Trade mark details block (like the old wizard payment screen) */}
          <div className="mb-6">
            <H3 className="mb-3 text-sm font-semibold uppercase tracking-wide text-gray-500">
              {t('payments.trademarkDetails', 'Trade mark details')}
            </H3>
            <div className="bg-gray-50 p-4 rounded-md">
              <Stack spacing={2}>
                {currentApplication?.trademarkName && (
                  <div className="flex justify-between gap-4 text-sm">
                    <Text className="text-gray-600 flex-shrink-0">{t('payments.markName', 'Trade mark')}</Text>
                    <Text className="font-medium text-gray-900 text-right break-words min-w-0">{currentApplication.trademarkName}</Text>
                  </div>
                )}
                {currentApplication?.trademarkType && (
                  <div className="flex justify-between gap-4 text-sm">
                    <Text className="text-gray-600 flex-shrink-0">{t('payments.markType', 'Type')}</Text>
                    <Text className="text-gray-900 text-right">{capitalize(currentApplication.trademarkType)}</Text>
                  </div>
                )}
                {/* Binds live to the applicant name entered below — never the
                    trade mark name */}
                <div className="flex justify-between gap-4 text-sm">
                  <Text className="text-gray-600 flex-shrink-0">{t('payments.applicant', 'Applicant')}</Text>
                  {applicantInfo.name ? (
                    <Text className="text-gray-900 text-right break-words min-w-0">{applicantInfo.name}</Text>
                  ) : (
                    <Text className="text-gray-400 italic text-right min-w-0">{t('payments.applicantTbc', 'To be confirmed')}</Text>
                  )}
                </div>
                {pricingRecord?.selectedCountries?.length > 0 && (
                  <div className="flex flex-col gap-0.5 text-sm sm:flex-row sm:justify-between sm:gap-4">
                    <Text className="text-gray-600 flex-shrink-0">{t('payments.countries', 'Countries')}</Text>
                    <Text className="text-gray-900 sm:text-right break-words min-w-0">
                      {pricingRecord.selectedCountries.map(countryDisplayName).filter(Boolean).join(', ')}
                    </Text>
                  </div>
                )}
                {pricingRecord?.selectedClasses?.length > 0 && (
                  <div className="flex flex-col gap-0.5 text-sm sm:flex-row sm:justify-between sm:gap-4">
                    <Text className="text-gray-600 flex-shrink-0">{t('payments.classes', 'Classes')}</Text>
                    <Text className="text-gray-900 sm:text-right break-words min-w-0">
                      {pricingRecord.selectedClasses.map(classDisplayLabel).filter(Boolean).join(', ')}
                    </Text>
                  </div>
                )}
              </Stack>
            </div>
          </div>

          {/* Line items */}
          <H3 className="mb-3 text-sm font-semibold uppercase tracking-wide text-gray-500">
            {t('payments.breakdown', 'Quote breakdown')}
          </H3>
          <div className="bg-gray-50 p-4 rounded-md">
            <Stack spacing={3}>
              {lineItems.map((item, index) => (
                <div key={index} className="flex justify-between gap-4">
                  <Text className="min-w-0 break-words">{item.description}</Text>
                  <Text className={`text-right whitespace-nowrap flex-shrink-0 ${item.amount < 0 ? 'text-green-600' : ''}`}>
                    {item.amount < 0 ? '-' : ''}{formatAmount(Math.abs(item.amount))}
                  </Text>
                </div>
              ))}
              <Divider />
              <div className="flex justify-between gap-4 font-semibold text-lg">
                <Text>{t('payments.total', 'Total')}</Text>
                <Text className="whitespace-nowrap">{formatAmount(quoteTotal)}</Text>
              </div>
              <Text className="text-xs text-gray-500">
                {t('payments.chargedIn', 'Charged in {{currency}}', { currency })}
              </Text>
            </Stack>
          </div>
        </CardContent>
      </Card>

      {/* Report — collapsed by default. Own component + own query so neither
          expansion toggles nor report fetches re-render the payment cards or
          remount the Embedded Checkout. */}
      {status === 'quote_sent' && <CollapsibleReport applicationId={currentApplicationId} />}

      {/* Applicant details — name + postal address, autosaved as they type.
          IP Australia requires these to file; the overlay below stays locked
          until the essentials are in. */}
      <div ref={applicantRef}>
        <ApplicantDetailsCard
          applicationId={currentApplicationId}
          application={currentApplication}
          fallbackName={applicantFallbackName}
          countryName={quoteCountryName}
          onApplicantChange={handleApplicantChange}
        />
      </div>

      {/* Declaration — single checkbox, signature auto-filled */}
      <div ref={declarationRef}>
        <DeclarationCard
          applicationId={currentApplicationId}
          declaration={declaration}
          isLoading={declarationLoading}
          signerName={signerName}
        />
      </div>

      {/* Payment — Embedded Checkout loads immediately, soft-gated by the
          declaration overlay (no clicks to reach payment) */}
      <Card>
        <CardHeader>
          <CardTitle>{tPayment('title', 'Complete Payment')}</CardTitle>
          <CardDescription>{tPayment('subtitle', 'Secure payment processing powered by Stripe')}</CardDescription>
        </CardHeader>
        <CardContent>
          {currentApplication?.paymentStatus === 'pending' && (
            <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-md">
              <div className="flex items-center gap-2 text-blue-700">
                <AlertCircle className="h-4 w-4" />
                <Text className="text-sm font-medium">
                  {t('payments.pendingRetry', 'A previous payment attempt is pending. You can proceed to try again.')}
                </Text>
              </div>
            </div>
          )}

          <div className="relative">
            {error ? (
              <div className="p-4 bg-red-50 border border-red-200 rounded-md">
                <div className="flex items-start gap-3">
                  <AlertCircle className="h-5 w-5 text-red-500 flex-shrink-0 mt-0.5" />
                  <div>
                    <Text className="text-sm text-red-700">{error}</Text>
                    <Button
                      onClick={handlePay}
                      disabled={isCreatingSession}
                      className="mt-3 px-4 py-2 bg-[#004d40] hover:bg-[#00695c] text-white text-sm font-medium rounded-md"
                    >
                      {t('payments.paymentRetry', 'Try again')}
                    </Button>
                  </div>
                </div>
              </div>
            ) : checkoutOptions ? (
              <EmbeddedCheckoutProvider
                key={clientSecret}
                stripe={stripePromise}
                options={checkoutOptions}
              >
                <EmbeddedCheckout />
              </EmbeddedCheckoutProvider>
            ) : (
              <div className="py-10 text-center">
                <Loader2 className="h-8 w-8 text-[#00796b] animate-spin mx-auto mb-3" />
                <Text className="text-sm text-gray-600">
                  {t('payments.settingUpPayment', 'Setting up secure payment…')}
                </Text>
              </div>
            )}

            {/* Soft gate: overlay-unlock pattern from the old guest checkout.
                Requires BOTH the applicant essentials (name, street, city,
                postcode) AND the declaration tick; the copy lists what is
                still missing and updates live. */}
            {(!declarationCompleted || !applicantInfo.complete) && !error && (
              <button
                type="button"
                onClick={scrollToBlocking}
                className="absolute inset-0 z-10 w-full rounded-md bg-white/70 backdrop-blur-[2px] flex items-center justify-center"
              >
                <span className="text-center px-6">
                  <Lock className="h-6 w-6 text-gray-400 mx-auto mb-2" />
                  <span className="block font-medium text-gray-800">
                    {!applicantInfo.complete && !declarationCompleted
                      ? t('payments.overlayUnlockBoth', 'Add your applicant details and tick the declaration above to pay')
                      : !applicantInfo.complete
                        ? t('payments.overlayUnlockApplicant', 'Add your applicant details above to pay')
                        : t('payments.overlayUnlock', 'Tick the declaration above to pay')}
                  </span>
                </span>
              </button>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default PaymentsSection;
